import styles from "./styles.module.scss";
import clsx from "clsx";
import { useRef } from "react";
import * as d3Sankey from "d3-sankey";
import { ChartHeader } from "@/components/ChartHeader";
import { Tooltip, useTooltip } from "@/components/Tooltip";
import { NoData } from "@/components/NoData";
import {
  formatDisplayList,
  formatMoney,
  formatMoneySr,
  getEconomyDisplayName,
  getSectorDisplayName,
  getSubSectorDisplayName,
} from "@/utils/format";
import sectorOptions from "@/config/trade-network/sectors";
import economies from "@/config/economies";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import economyRegions from "@/config/economy-regions";
import regionColors from "@/config/region-colors";
import colors from "@/config/colors";

import type { DataRow } from "../App";

interface NodeRow {
  id: string;
  economyCode: keyof typeof economies;
}

interface LinkRow {
  source: keyof typeof economies;
  target: keyof typeof economies;
  value: number;
}

type SNode = d3Sankey.SankeyNode<NodeRow, LinkRow>;
type SLink = d3Sankey.SankeyLink<NodeRow, object>;

interface SankeyProps {
  data: DataRow[];
  worldData: DataRow[];
  sector: (typeof sectorOptions)[number];
  subSector: string;
  year: string;
  topFlowsPercent: number;
}

function getLinkProperty<T extends keyof NodeRow>(
  source: string | number | d3Sankey.SankeyNode<NodeRow, object>,
  property: T
) {
  return (
    typeof source === "string" || typeof source === "number"
      ? source.toString()
      : source[property]
  ) as NodeRow[T];
}

const isEconomyCode = (code: string): code is keyof typeof economies => {
  return code in economies;
};

export function SankeyChart({
  data,
  worldData,
  sector,
  subSector,
  year,
  topFlowsPercent,
}: SankeyProps) {
  // Sankey nodes (start and end points)

  // Viewport width checks
  const is1600px = useMediaQuery("(min-width: 1600px)");
  const is1200px = useMediaQuery("(min-width: 1200px)");
  const is1024px = useMediaQuery("(min-width: 1024px)");
  const is600px = useMediaQuery("(min-width: 600px)");

  // Viewport height checks
  const is1200pxTall = useMediaQuery("(min-height: 1200px)");
  const is1000pxTall = useMediaQuery("(min-height: 1000px)");
  const is800pxTall = useMediaQuery("(min-height: 800px)");
  const is700pxTall = useMediaQuery("(min-height: 700px)");

  const nodes: SNode[] = [];
  data.forEach((flow) => {
    // To avoid a 'circular' link' error, we need to preserve the source/target distinction even though economy names will overlap
    const sourceSlug = `${flow["Partner code"]}-source`;
    const targetSlug = `${flow["Reporter code"]}-target`;

    if (
      !nodes.find((node) => node.id === sourceSlug) &&
      isEconomyCode(flow["Partner code"])
    ) {
      nodes.push({
        id: sourceSlug,
        economyCode: flow["Partner code"],
      });
    }

    if (
      !nodes.find((node) => node.id === targetSlug) &&
      isEconomyCode(flow["Reporter code"])
    ) {
      nodes.push({
        id: targetSlug,
        economyCode: flow["Reporter code"],
      });
    }
  });

  // Sankey links (paths)
  const links = data.map((flow) => {
    return {
      source: `${flow["Partner code"]}-source`,
      target: `${flow["Reporter code"]}-target`,
      value: flow["Value"],
    };
  }) as SLink[];

  const vertPadding = is1200px ? 30 : is1024px ? 38 : 45;
  const horizInset = 180;
  const textLabelPadding = 15;
  const width = 1200;

  // Mobile
  let baseHeight = 900;

  // Desktop (unscaled)
  if (is1024px) {
    baseHeight = is1200pxTall
      ? 1000
      : is1000pxTall
      ? 900
      : is800pxTall
      ? 700
      : is700pxTall
      ? 600
      : 450;
  }

  // Desktop wide layout (scaled up, so subtract from above values)
  if (is1600px) {
    baseHeight -= 150;
  }

  const height = baseHeight - vertPadding * 2;

  const sankeyRef = useRef<HTMLDivElement>(null);

  const {
    showTooltip,
    moveTooltip,
    hideTooltip,
    tooltipProps,
    tooltipVisible,
    tooltipData,
  } = useTooltip<
    { type: "link"; content: LinkRow } | { type: "node"; content: NodeRow }
  >(sankeyRef.current);

  if (nodes.length === 0 || links.length === 0) {
    return null;
  }

  // Construct array of total export values for each node (economy)
  const nodesWithTotals = nodes.map((node) => {
    // Get the links for which this node is the source (if it's an exporter)
    const nodeExportLinks = links.filter((link) => link.source === node.id);
    // Get the links for which this node is the source (if it's an importer)
    const nodeImportLinks = links.filter((link) => link.target === node.id);

    return {
      ...node,
      totalExports: nodeExportLinks.reduce(
        (total, link) => total + (link.value || 0),
        0
      ),
      totalImports: nodeImportLinks.reduce(
        (total, link) => total + (link.value || 0),
        0
      ),
    };
  });

  const sankey = d3Sankey
    .sankey()
    .nodeId((node) => (node as (typeof nodesWithTotals)[number]).id) // Needed to avoid "Error: Missing: myNode"
    .nodeWidth(8)
    .nodePadding(15)
    .extent([
      [1, 1],
      [width - horizInset * 2, height - 6],
    ])
    .nodeAlign(d3Sankey.sankeyLeft)
    .nodeSort((a, b) => {
      const aNode = a as (typeof nodesWithTotals)[number];
      const bNode = b as (typeof nodesWithTotals)[number];
      return (
        bNode.totalExports - aNode.totalExports ||
        bNode.totalImports - aNode.totalImports
      );
    })
    .linkSort((a, b) => (b as LinkRow).value - (a as LinkRow).value);

  // Run our data through the sankey() function
  // This *mutates* the data in nodes and links
  sankey({
    nodes: nodesWithTotals,
    links: links,
  });

  const displaySector = getSectorDisplayName(sector);
  const displaySubSector = getSubSectorDisplayName(subSector);

  const sectorName =
    displaySubSector.toLocaleLowerCase() === "total"
      ? displaySector
      : displaySubSector;

  const sortedLinks = links.sort((a, b) => b.value - a.value);

  const tooltipWorldSource =
    tooltipData?.type === "link" && tooltipData.content.source
      ? worldData.find(
          (flow) =>
            flow["Reporter code"] === "WLD" &&
            flow["Partner code"] === tooltipData.content.source
        )
      : null;
  const tooltipWorldTarget =
    tooltipData?.type === "link" && tooltipData?.content.target
      ? worldData.find(
          (flow) =>
            flow["Reporter code"] === tooltipData.content.target &&
            flow["Partner code"] === "WLD"
        )
      : null;

  const tooltipNodeTotal =
    tooltipData?.type === "node" && tooltipData?.content.economyCode
      ? data.reduce((total, flow) => {
          if (
            tooltipData?.content.id.endsWith("-source") &&
            flow["Partner code"] === tooltipData?.content.economyCode
          ) {
            total += flow["Value"];
          } else if (
            tooltipData?.content.id.endsWith("-target") &&
            flow["Reporter code"] === tooltipData?.content.economyCode
          ) {
            total += flow["Value"];
          }

          return total;
        }, 0)
      : null;
  const tooltipWorldNodeTotal =
    tooltipData?.type === "node" && tooltipData?.content.economyCode
      ? worldData.find((flow) =>
          tooltipData?.content.id.endsWith("-source")
            ? flow["Reporter code"] === "WLD" &&
              flow["Partner code"] === tooltipData?.content.economyCode
            : flow["Reporter code"] === tooltipData?.content.economyCode &&
              flow["Partner code"] === "WLD"
        )
      : null;

  return (
    <div className={styles["sankey-wrap"]} ref={sankeyRef}>
      <ChartHeader
        title={`${sectorName} trade corridors by value`}
        description={`See the top 40 trade corridors of ${sectorName.toLowerCase()} in ${year} by value, which account for ${topFlowsPercent}% of trade.`}
      />

      {!nodesWithTotals.length ? (
        <NoData />
      ) : (
        <>
          {sortedLinks.length ? (
            <span
              className={styles.hide}
            >{`The top trade corridor is from ${getEconomyDisplayName(
              getLinkProperty(sortedLinks[0].source, "id").replace(
                "-source",
                ""
              )
            )} to ${getEconomyDisplayName(
              getLinkProperty(sortedLinks[0].target, "id").replace(
                "-target",
                ""
              )
            )} with ${formatMoneySr(
              sortedLinks[0].value
            )}, followed by ${formatDisplayList(
              sortedLinks.map(
                (link) =>
                  `${getEconomyDisplayName(
                    getLinkProperty(link.source, "id").replace("-source", "")
                  )} to ${getEconomyDisplayName(
                    getLinkProperty(link.target, "id").replace("-target", "")
                  )} with ${formatMoneySr(link.value)}`
              )
            )}.`}</span>
          ) : null}
          <svg viewBox={`0 0 ${width} ${height + vertPadding * 2}`} aria-hidden>
            <g
              className={styles["sankey-headings"]}
              transform={`translate(0, ${vertPadding / 2})`}
            >
              <text textAnchor="start" x={horizInset}>
                Exporters
              </text>
              <text textAnchor="end" x={width - horizInset}>
                Importers
              </text>
            </g>
            <g transform={`translate(0, ${vertPadding})`}>
              <g className={styles["sankey-nodes"]}>
                {nodesWithTotals.length
                  ? nodesWithTotals.map((node) => {
                      const { x0 = 0, x1 = 0, y0 = 0, y1 = 0 } = node;
                      const halfWidth = width / 2;
                      return (
                        <g
                          key={node.id}
                          className={styles["sankey-node"]}
                          onMouseEnter={(event) => {
                            showTooltip(event, {
                              type: "node",
                              content: node,
                            });
                          }}
                          onMouseMove={(event) => {
                            moveTooltip(event);
                          }}
                          onMouseLeave={() => {
                            hideTooltip();
                          }}
                        >
                          <rect
                            x={x0 + horizInset}
                            y={y0}
                            width={x1 - x0}
                            height={y1 - y0}
                            style={{
                              fill: colors[
                                regionColors[
                                  economyRegions[
                                    node.economyCode as keyof typeof economyRegions
                                  ]
                                ]
                              ],
                            }}
                          />
                          <text
                            x={
                              (node.x0 || 0) < width / 2
                                ? x1 + horizInset - textLabelPadding
                                : x0 + horizInset + textLabelPadding
                            }
                            y={(y1 + y0) / 2 + 7.5}
                            textAnchor={x0 < halfWidth ? "end" : "start"}
                          >
                            {getEconomyDisplayName(node.economyCode)}
                          </text>
                        </g>
                      );
                    })
                  : null}
              </g>

              <g
                className={styles["sankey-links"]}
                transform={`translate(${horizInset}, 0)`}
              >
                {links.length
                  ? links.map((link) => {
                      const isActive =
                        tooltipData?.type === "node" &&
                        ((tooltipData.content.id ==
                          getLinkProperty(link.source, "id") &&
                          tooltipData.content.id?.includes("source")) ||
                          (tooltipData.content.id ==
                            getLinkProperty(link.target, "id") &&
                            tooltipData.content.id?.includes("target")));

                      return (
                        <path
                          key={`${getLinkProperty(
                            link.source,
                            "id"
                          )}-${getLinkProperty(link.target, "id")}`}
                          d={d3Sankey.sankeyLinkHorizontal()(link) || undefined}
                          className={clsx(
                            styles["sankey-link"],
                            isActive && styles["sankey-link-active"]
                          )}
                          strokeWidth={Math.max(1, link.width || 0)}
                          onMouseEnter={(event) => {
                            showTooltip(event, {
                              type: "link",
                              content: {
                                source: getLinkProperty(
                                  link.source,
                                  "economyCode"
                                ),
                                target: getLinkProperty(
                                  link.target,
                                  "economyCode"
                                ),
                                value: link.value,
                              },
                            });
                          }}
                          onMouseMove={(event) => {
                            moveTooltip(event);
                          }}
                          onMouseLeave={() => {
                            hideTooltip();
                          }}
                        />
                      );
                    })
                  : null}
              </g>
            </g>
          </svg>
          {tooltipVisible && tooltipData ? (
            <Tooltip
              {...tooltipProps}
              style={{
                ...tooltipProps.style,
                width: is1024px ? 350 : is600px ? 300 : 250,
              }}
            >
              <div className={styles.tooltip}>
                <h4 className={styles["tooltip-heading"]}>{sectorName}</h4>

                {tooltipData.type === "link" ? (
                  <>
                    <p className={styles["tooltip-title"]}>
                      Exports from{" "}
                      {getEconomyDisplayName(tooltipData.content.source)} to{" "}
                      {getEconomyDisplayName(tooltipData.content.target)}
                    </p>
                    <p className={styles["tooltip-value"]}>
                      {formatMoney(tooltipData.content.value)}
                    </p>
                    {tooltipWorldSource?.Value ? (
                      <p className={styles["tooltip-secondary"]}>
                        Total{" "}
                        {getEconomyDisplayName(tooltipData.content.source)}{" "}
                        exports: {formatMoney(tooltipWorldSource.Value)}
                      </p>
                    ) : null}
                    {tooltipWorldTarget?.Value ? (
                      <p className={styles["tooltip-secondary"]}>
                        Total{" "}
                        {getEconomyDisplayName(tooltipData.content.target)}{" "}
                        imports: {formatMoney(tooltipWorldTarget.Value)}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <p className={styles["tooltip-title"]}>
                      Top{" "}
                      {tooltipData.content.id.endsWith("-source")
                        ? "exports from"
                        : "imports to"}{" "}
                      {getEconomyDisplayName(tooltipData.content.economyCode)}
                    </p>
                    {tooltipNodeTotal ? (
                      <p className={styles["tooltip-value"]}>
                        {formatMoney(tooltipNodeTotal)}
                      </p>
                    ) : null}
                    {tooltipWorldNodeTotal ? (
                      <p className={styles["tooltip-secondary"]}>
                        Total{" "}
                        {getEconomyDisplayName(tooltipData.content.economyCode)}{" "}
                        {tooltipData.content.id.endsWith("-source")
                          ? "exports"
                          : "imports"}
                        : {formatMoney(tooltipWorldNodeTotal.Value)}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </Tooltip>
          ) : null}
        </>
      )}
    </div>
  );
}
